import { useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { DefaultButton, DownloadButton, FilterButton } from "../ui/button";
import { Anchor } from "../ui/anchor";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { FormContainer } from "../form/form-container";
import { FieldInput } from "../form/field-input";
import { swal } from "@/utils/lib/swal";
import { DataConfig } from "./useDefaultDataConfig";

interface TableFormProps extends React.PropsWithChildren {
  setDataConfig: React.Dispatch<React.SetStateAction<DataConfig>>;
  form: UseFormReturn<FieldValues>;
  createPath?: string | null;
  createLabel?: string;
  editPath?: string | null;
  editLabel?: string;
  selectedIds?: string[];
  handleBulkDelete?: (() => void) | null;
  handleResetFilter?: (() => void) | null;
  isPendingDelete?: boolean;
  handleDownload?: (param: object) => void;
  isDownloading?: boolean;
}
export function TableForm({
  setDataConfig,
  form,
  children,
  createPath,
  createLabel,
  editPath,
  editLabel,
  selectedIds,
  handleBulkDelete,
  handleResetFilter,
  isPendingDelete,
  handleDownload,
  isDownloading,
}: TableFormProps) {
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({});

  function handleToggleFilter() {
    const search = form.getValues("search");
    form.reset();
    form.setValue("search", search);
    setShowFilter((prev) => !prev);
  }

  function handleFilter(values: FieldValues) {
    const newValues = removeUndefined(values);

    setFilters(newValues);

    const search = newValues.search;
    const query = showFilter ? newValues : { search };

    setDataConfig?.((prev) => ({
      ...query,
      limit: prev.limit,
      page: "0",
      asc: prev.asc,
      sort: prev.sort,
    }));
  }

  function handleReset() {
    form.reset();
    setFilters({});
    handleResetFilter?.();

    setDataConfig?.((prev) => ({
      limit: prev.limit,
      page: "0",
      asc: prev.asc,
      sort: prev.sort,
    }));
  }

  function handleDelete() {
    if (!selectedIds?.length) {
      return swal.fire({
        text: "You haven't selected any data",
        icon: "info",
      });
    }

    handleBulkDelete?.();
    setDataConfig?.((prev) => ({
      limit: prev.limit,
      page: "0",
      asc: prev.asc,
      sort: prev.sort,
    }));
  }

  const handleKeyDownSpecChar = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (["[", "]", "{", "}", "^", "#", "|", "\\"].includes(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <FormContainer
      form={form}
      onSubmit={handleFilter}
      className="flex flex-col gap-y-4"
    >
      <div className="flex items-end justify-end gap-x-2">
        <FieldInput
          name="search"
          placeholder="Search..."
          label="Search"
          hideLabel
          onKeyDown={handleKeyDownSpecChar}
        />
        {children && <FilterButton onClick={handleToggleFilter} />}
        {handleDownload && (
          <DownloadButton
            onClick={() => handleDownload(filters)}
            loading={isDownloading}
          />
        )}
        {editPath && (
          <Anchor mood="axiata-blue" href={editPath}>
            {editLabel ?? "Edit"}
          </Anchor>
        )}
        {createPath && (
          <Anchor mood="axiata-blue" href={createPath}>
            {createLabel ?? "Add"}
          </Anchor>
        )}
        {handleBulkDelete && (
          <DefaultButton
            type="button"
            mood="axiata-red"
            onClick={handleDelete}
            loading={isPendingDelete}
          >
            Delete
          </DefaultButton>
        )}
      </div>
      {showFilter && children && (
        <Card>
          <CardHeader>
            <CardTitle>Filter</CardTitle>
          </CardHeader>
          <CardContent className="max-w-6xl space-y-4">
            {children}
            <div className="flex gap-x-2">
              <DefaultButton type="submit" mood="axiata-blue">
                Apply
              </DefaultButton>
              <DefaultButton
                type="button"
                mood="axiata-blue-outline"
                onClick={handleReset}
              >
                Reset
              </DefaultButton>
            </div>
          </CardContent>
        </Card>
      )}
    </FormContainer>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function removeUndefined(obj: { [key: string]: string | undefined }) {
  const newObj: { [key: string]: string } = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      newObj[key] = obj[key] as string;
    }
  }
  return newObj;
}
